import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';

export const useNonprofitMonthlyActivityLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'Nonprofit Monthly Activity',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTIONS]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTIONS) || '',
      [EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTION_VALUE]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTION_VALUE) ||
        '',
    },
    formSchema: z.object({
      [EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTIONS]: z
        .string()
        .min(1, { message: tValidation('validation.required') }),
      [EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTION_VALUE]: z
        .string()
        .min(1, { message: tValidation('validation.required') }),
    }),
  });

  const handleSubmit = async () => {
    // Save both questions to local store
    saveEddQuestionToLocalStore({
      question: t('organic.nonprofitMonthlyActivity.transactionsQuestion'),
      questionId: EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTIONS,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTIONS)
      ),
    });
    saveEddQuestionToLocalStore({
      question: t('organic.nonprofitMonthlyActivity.valueQuestion'),
      questionId: EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTION_VALUE,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_MONTHLY_TRANSACTION_VALUE)
      ),
    });

    // Save to server
    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_NONPROFIT_MONTHLY_ACTIVITY);
    } catch (error) {
      console.error(error);
    }
  };

  const monthlyTransactionsOptions = [
    {
      value: 'Under 50',
      label: t('organic.nonprofitMonthlyActivity.under50'),
    },
    {
      value: '50 to 100',
      label: t('organic.nonprofitMonthlyActivity.50to100'),
    },
    {
      value: '101 to 250',
      label: t('organic.nonprofitMonthlyActivity.101to250'),
    },
    {
      value: '251 or more',
      label: t('organic.nonprofitMonthlyActivity.251orMore'),
    },
  ];

  const monthlyVolumeOptions = [
    {
      value: 'Under $10,000',
      label: t('organic.nonprofitMonthlyActivity.under10k'),
    },
    {
      value: '$10,001 to $50,000',
      label: t('organic.nonprofitMonthlyActivity.10kto50k'),
    },
    {
      value: '$50,001 to $100,000',
      label: t('organic.nonprofitMonthlyActivity.50kto100k'),
    },
    {
      value: '$100,001 to $500,000',
      label: t('organic.nonprofitMonthlyActivity.100kto500k'),
    },
    {
      value: '$500,001 to $1,000,000',
      label: t('organic.nonprofitMonthlyActivity.500kto1m'),
    },
  ];

  return {
    formMethods,
    monthlyTransactionsOptions,
    monthlyVolumeOptions,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
